import { Component, Input, OnInit, SkipSelf } from '@angular/core';
import {
  ControlContainer,
  FormGroup,
  FormGroupDirective,
} from '@angular/forms';
import { Iban, Owner } from '@app/dao/user/modals';
import { UserDao } from '@app/dao/user/user.dao';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { UserService } from '@app/providers/user/user.service';
import { Observable, switchMap, tap } from 'rxjs';

@Component({
    selector: 'debit',
    templateUrl: './debit.component.html',
    styleUrls: ['./debit.component.scss'],
    viewProviders: [
        {
            provide: ControlContainer,
            useFactory: (container: ControlContainer) => container,
            deps: [[new SkipSelf(), ControlContainer]],
        },
    ],
    standalone: false
})
export class DebitComponent implements OnInit {
  @Input() derrors = false;
  @Input() control = 'directDebitIban';
  $ibans: Observable<Iban[]>;
  ibansObects: Iban[];
  selected = 'MO99999999999999999999999999999999';
  owners: Owner[] = [];

  constructor(
    private controlContainer: FormGroupDirective,
    private subscriptionFacade: SubscriptionFacadeService,
    private userService: UserService,
    private userDao: UserDao,
  ) {}
  get form() {
    // if the control is nested in a form group
    if (this.control.includes('.')) {
      const keys = this.control.split('.');
      let currentFormGroup: FormGroup = this.controlContainer.form;
      keys.forEach(key => {
        if (currentFormGroup && currentFormGroup.get(key)) {
          const next = currentFormGroup.get(key);
          if (next instanceof FormGroup) {
            currentFormGroup = next;
          }
        }
      });

      return currentFormGroup;
    }

    return this.controlContainer.form?.controls?.[this.control];
  }
  ngOnInit(): void {
    this.getIban();

    // Also listen for form value changes to update when IBAN is set
    setTimeout(() => {
      const ibanControl = this.form?.get('iban');
      if (ibanControl) {
        // Check initial value
        const initialValue = ibanControl.value;
        if (initialValue && this.ibansObects) {
          this.matchAndPopulateIban(initialValue);
        }
      }
    }, 100);
  }

  setSelectedValue(value: Iban) {
    this.selected = value.iban;
  }

  onInputChange($event) {
    this.selected = $event.value;
    const ibanSelected = this.ibansObects.find(v => v.iban === this.selected);

    const { id, iban, bankName, bic, ownerIds } = ibanSelected;

    this.form.get('id').setValue(id);
    this.form.get('holder').setValue(this.getOwnerLabel(ownerIds));
    this.form.get('bankName').setValue(bankName);
    this.form.get('bic').setValue(bic);
    this.form.get('iban').setValue(iban);
    this.form.get('ownerIds').setValue(ownerIds);
  }

  getOwnerLabel(ownerIds) {
    const owner = this.owners.find(o => o?.id === ownerIds[0]);
    return owner?.label || '';
  }

  private getIban() {
    const pledge = this.subscriptionFacade.getPledge();

    this.$ibans = this.userDao.getOwners().pipe(
      tap(owner => {
        this.owners = owner;
      }),
      switchMap(_ => {
        return this.userService.getOwnerIbans(pledge?.accountId as number).pipe(
          tap(ibans => {
            // Store ibans first
            this.ibansObects = ibans;
            // Then try to match the current form value
            const currentIbanValue = this.form?.get('iban')?.value;
            if (currentIbanValue) {
              this.matchAndPopulateIban(currentIbanValue);
            }
          }),
        );
      }),
    );
  }

  private matchAndPopulateIban(ibanValue: string) {
    if (!ibanValue || !this.ibansObects) {
      return;
    }

    const result = this.ibansObects.find(
      b => b.iban?.replace(/\s/g, '') === ibanValue?.replace(/\s/g, ''),
    );

    if (result) {
      this.setSelectedValue(result);
      const { id, iban, bankName, bic, ownerIds } = result;
      this.form.get('id')?.setValue(id, { emitEvent: false });
      this.form
        .get('holder')
        ?.setValue(this.getOwnerLabel(ownerIds), { emitEvent: false });
      this.form.get('bankName')?.setValue(bankName, { emitEvent: false });
      this.form.get('bic')?.setValue(bic, { emitEvent: false });
      this.form.get('iban')?.setValue(iban, { emitEvent: false });
      this.form.get('ownerIds')?.setValue(ownerIds, { emitEvent: false });
    }
  }
}
